import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Table from 'Components/Table';
import { Box } from 'Components/Base';
import {
  formatAttemptDateTime,
  formatQuizFeedAccuracy,
  formatQuizFeedScore,
  getStudentDisplayName,
} from 'Domains/adaptiveQuiz';
import { TableHead } from 'Containers/AdaptiveQuiz/AdaptiveQuizTable/AdaptiveQuizTable';

const TextWrapper = styled(Box)`
  text-align: center;
  white-space: pre-line;
`;

/**
 * NOTE: react-table renders an empty placeholder cell above each column which is not in a group,
 * so the header of column 1-6 is moved up to the middle of both header rows,
 * and only the group header (7th cell of the 1st row) has a bottom border.
 */
const AdaptiveQuizAttemptTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 12px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head .MuiTableCell-sizeSmall {
    padding: 6px 10px;
  }

  & .MuiTableRow-head:nth-child(1) .MuiTableCell-head:nth-child(7) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  }

  & .MuiTableRow-head:nth-child(2) .MuiTableCell-head:nth-child(-n + 6) > div {
    position: relative;
    top: -18px;
  }
`;

export const AdaptiveQuizAttemptTable = ({ data, maxHeight, ...props }) => {
  const { t } = useTranslation('adaptiveQuiz');
  const { attempts = [], classroomUsername = '' } = data;
  const displayName = getStudentDisplayName(data);

  const columns = [
    {
      id: 'attemptNo',
      Header: <TableHead>{t('ATTEMPT_NO')}</TableHead>,
      accessor: 'attemptNo',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'50px'}>{value}</TextWrapper>,
    },
    {
      id: 'unit',
      Header: <TableHead>{t('UNIT')}</TableHead>,
      accessor: properties => `${properties.courseName}\n(${properties.courseCode})`,
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'160px'}>{value}</TextWrapper>,
    },
    {
      id: 'section',
      Header: <TableHead>{t('SECTION')}</TableHead>,
      accessor: 'lessonName',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'120px'}>{value}</TextWrapper>,
    },
    {
      id: 'username',
      Header: <TableHead>{t('USERNAME')}</TableHead>,
      accessor: () => classroomUsername,
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'110px'}>{value}</TextWrapper>,
    },
    {
      id: 'displayName',
      Header: <TableHead>{t('DISPLAY_NAME')}</TableHead>,
      accessor: () => displayName,
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <TextWrapper minWidth={'160px'} style={{ textAlign: 'left' }}>
          {value}
        </TextWrapper>
      ),
    },
    {
      id: 'dateTime',
      Header: <TableHead>{t('DATE_TIME')}</TableHead>,
      accessor: properties => formatAttemptDateTime(properties.startedAt, properties.endedAt),
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'150px'}>{value}</TextWrapper>,
    },
    {
      id: 'questionCount',
      Header: <TableHead>{t('QUESTION_COUNT')}</TableHead>,
      columns: [
        {
          id: 'adaptiveQuiz',
          Header: <TableHead>{t('ADAPTIVE_QUIZ')}</TableHead>,
          accessor: 'adaptiveQuiz',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => <TextWrapper>{formatQuizFeedScore(value)}</TextWrapper>,
        },
        {
          id: 'adaptiveQuizAccuracy',
          Header: <TableHead>{t('ACCURACY')}</TableHead>,
          accessor: 'adaptiveQuiz',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => <TextWrapper>{formatQuizFeedAccuracy(value)}</TextWrapper>,
        },
      ],
    },
  ];

  return (
    <AdaptiveQuizAttemptTableStyle
      dense
      stickyHeader
      columns={columns}
      data={attempts}
      style={{ maxHeight }}
      {...props}
    />
  );
};

AdaptiveQuizAttemptTable.propTypes = {
  data: PropTypes.shape({
    attempts: PropTypes.array.isRequired,
    classroomUsername: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AdaptiveQuizAttemptTable;
